import { Link } from "react-router-dom";
import type { Book, SeriesPlace } from "../api";
import { useT } from "../i18n";

/**
 * SeriesLine says which volume a book is: "Vol. 2 of 6 · <series>", or, where
 * a series stands for itself once (Browse, 0119 AC 5), "6 volumes · <series>".
 * The numbers are the server's, counted over what this reader may read.
 *
 * The line is interface text with the series name inside it, so the name is a
 * <bdi> rather than the line carrying dir="auto" (CLAUDE.md: a Persian name in
 * an English line, or the reverse, must not swap the separator's side).
 */
export default function SeriesLine({
  series,
  count = false,
  link = false,
  className = "",
  testID = "series-line",
}: {
  series: SeriesPlace;
  /** Say how many volumes, not which one this is. */
  count?: boolean;
  /** Link the name to the series' page; off inside a tile, which is a link already. */
  link?: boolean;
  className?: string;
  testID?: string;
}) {
  const t = useT();
  const place = count ? t.series.volumes(series.of) : t.series.volumeOf(series.number, series.of);
  const name = <bdi>{series.name}</bdi>;
  return (
    <p data-testid={testID} className={className}>
      {place} ·{" "}
      {link ? (
        <Link to={`/collections/${series.id}`} className="underline-offset-2 hover:underline">
          {name}
        </Link>
      ) : (
        name
      )}
    </p>
  );
}

/**
 * tileHref is where a book's tile leads. Where a series shows once (Browse,
 * `seriesCount`), the volume's tile stands for its series — it says
 * "m volumes" — so it opens the series page (owner, 10-19); every other tile
 * opens its book. One rule for the grid tile and the featured book.
 */
export function tileHref(book: Book, seriesCount: boolean): string {
  return seriesCount && book.series ? `/collections/${book.series.id}` : `/books/${book.id}`;
}

/**
 * SeriesNav is a book page's place in its series and the way to the volumes
 * either side of it (0119 AC 4). The neighbours are the readable ones, so the
 * links skip a volume this reader may not open rather than lead to a 404.
 */
export function SeriesNav({ series }: { series: SeriesPlace }) {
  const t = useT();
  const neighbour = (to: { id: string; title: string }, label: string, testID: string) => (
    <Link
      to={`/books/${to.id}`}
      data-testid={testID}
      className="min-w-0 truncate rounded-full border border-line px-3 py-1 hover:border-sage"
    >
      {label}: <bdi>{to.title}</bdi>
    </Link>
  );
  return (
    <div data-testid="series-nav" className="mt-2 text-xs">
      <SeriesLine series={series} link className="font-medium text-sage-deep" />
      {(series.previous || series.next) && (
        <div className="mt-2 flex flex-wrap justify-center gap-2 text-ink-soft">
          {series.previous && neighbour(series.previous, t.series.previous, "series-previous")}
          {series.next && neighbour(series.next, t.series.next, "series-next")}
        </div>
      )}
    </div>
  );
}
